import { useRef, useState } from 'react'
import { Circle, GoogleMap, Marker } from '@react-google-maps/api'
import { Map as MapIcon, Satellite } from 'lucide-react'

/**
 * The map the form has under its address: click or drag the pin to say where
 * the listing is, and drag the circle's edge to set how wide an area is shown
 * instead of the exact spot.
 */

/** The narrowest and widest circle the map lets an agent draw, in metres. */
export const MIN_RADIUS = 50
export const MAX_RADIUS = 5000

const MALAYSIA = { lat: 4.2105, lng: 101.9758 }
const BLUE = '#4285F4'

interface Props {
    lat: number | null
    lng: number | null
    radius: number | null
    disabled: boolean
    onMove: (lat: number, lng: number) => void
    onRadius: (radius: number) => void
}

export default function LocationMap({ lat, lng, radius, disabled, onMove, onRadius }: Props) {
    const [satellite, setSatellite] = useState(false)
    // Letting go of the circle's edge ends in a click, which is not a click
    // on the map: one that lands just after the circle was resized is ignored.
    const resizedAt = useRef(0)
    const justResized = () => Date.now() - resizedAt.current < 400
    const circle = useRef<google.maps.Circle | null>(null)
    const pinned = typeof lat === 'number' && typeof lng === 'number'
    const centre = pinned ? { lat: lat as number, lng: lng as number } : MALAYSIA
    const shown = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, radius || MIN_RADIUS))

    const moved = (e: google.maps.MapMouseEvent) => {
        if (disabled || !e.latLng) return
        onMove(e.latLng.lat(), e.latLng.lng())
    }

    return (
        <div className='relative my-2 overflow-hidden rounded-md border border-gray-200'>
            <button
                type='button'
                onClick={() => setSatellite((s) => !s)}
                className='absolute left-2 top-2 z-10 flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-md hover:bg-gray-100'>
                {satellite ? <MapIcon className='h-3.5 w-3.5' /> : <Satellite className='h-3.5 w-3.5' />}
                {satellite ? 'Map' : 'Satellite'}
            </button>
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '280px' }}
                center={centre}
                zoom={pinned ? 15 : 6}
                options={{
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    draggableCursor: disabled ? undefined : 'pointer',
                    mapTypeId: satellite ? 'hybrid' : 'roadmap',
                }}
                onClick={(e) => {
                    if (!justResized()) moved(e)
                }}>
                {pinned && (
                    <>
                        <Circle
                            center={centre}
                            radius={shown}
                            options={{ fillColor: BLUE, fillOpacity: 0.2, strokeColor: BLUE, strokeOpacity: 0.8, strokeWeight: 2, editable: !disabled, draggable: false }}
                            onLoad={(c) => {
                                circle.current = c
                            }}
                            onUnmount={() => {
                                circle.current = null
                            }}
                            onRadiusChanged={() => {
                                const c = circle.current
                                if (!c) return
                                resizedAt.current = Date.now()
                                const drawn = Math.round(c.getRadius())
                                const kept = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, drawn))
                                if (kept !== drawn) c.setRadius(kept)
                                if (kept !== shown) onRadius(kept)
                            }}
                            onCenterChanged={() => {
                                // The handle in the middle of the circle moves the pin with it.
                                const at = circle.current?.getCenter()
                                if (!at || disabled) return
                                if (Math.abs(at.lat() - centre.lat) > 1e-7 || Math.abs(at.lng() - centre.lng) > 1e-7) onMove(at.lat(), at.lng())
                            }}
                            // A click inside the circle is still a click on the map under it.
                            onClick={(e) => {
                                if (!justResized()) moved(e)
                            }}
                        />
                        <Marker
                            position={centre}
                            draggable={!disabled}
                            onDragEnd={moved}
                            icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: BLUE, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }}
                        />
                    </>
                )}
            </GoogleMap>
        </div>
    )
}
